import { z } from "zod";
import { auditInfoSchema } from "../common/schemas";

export const targetKinds = ["User", "Workspace"] as const;
export type AssignmentTargetKind = (typeof targetKinds)[number];

/**
 * Canonicalize an arbitrary target-kind string to the PascalCase form
 * the rest of the frontend speaks. The backend serializes enums as
 * camelCase ("user", "workspace") on the wire, but every consumer
 * here compares with PascalCase literals — without this normalizer
 * `row.targetKind === "User"` is `false` for every row from the API,
 * which silently breaks the assignments-list filter (no user names
 * resolve), the row renderer (`describeTarget` falls into the wrong
 * branch), and the AssetForm's "current target" label. Saves go in
 * fine because the API's `TryParseTargetKind` is case-insensitive.
 *
 * Same fix shape as `canonicalAssetType` and `canonicalWorkspaceType`
 * — keep the wire ↔ frontend bridge in one place.
 */
export function canonicalAssignmentTargetKind(raw: string): string {
    const match = targetKinds.find((k) => k.toLowerCase() === raw.toLowerCase());
    return match ?? raw;
}

export const assetAssignmentViewSchema = z.object({
    id: z.string(),
    assetId: z.string(),
    targetKind: z.string().transform(canonicalAssignmentTargetKind),
    assignedToUserId: z.string().nullable().optional(),
    assignedToWorkspaceId: z.string().nullable().optional(),
    assigned: auditInfoSchema,
    released: auditInfoSchema.nullable().optional(),
    isOpen: z.boolean(),
});
export type AssetAssignmentView = z.infer<typeof assetAssignmentViewSchema>;

export const assetAssignmentListResponseSchema = z.object({
    results: z.array(assetAssignmentViewSchema),
});

export const assetAssignmentMutationResponseSchema = z.object({
    assignment: assetAssignmentViewSchema,
});

export interface AssetAssignmentCreatePayload {
    readonly id: string;
    readonly assetId: string;
    readonly targetKind: AssignmentTargetKind;
    readonly userId?: string | null;
    readonly workspaceId?: string | null;
}
